import { useLayoutEffect } from 'react'
import { FlexBox } from 'shared/components/Base/Base'
import { Divider } from 'shared/components/Base/Divider'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { Text } from 'shared/components/Text/Text'
import { HandlerOf } from 'shared/helpers/typeHelper'
import { bgGray } from 'shared/theme/colors'
import styled from 'styled-components'

import { DeductionCreditReason, Repayment, VendorProgramPolicyType } from '../../models/repayments'
import { CorrectionAdvanceForm, CorrectionSubscriptionForm } from './PolicyCreditCorrectionForm'
import { DebtWriteOffForm } from './PolicyCreditDebtWriteOffForm'
import { ExternalPaymentForm } from './PolicyCreditExternalPaymentForm'

const DeductionCreditOptions = [
  { label: 'Correction', value: DeductionCreditReason.CORRECTION },
  { label: 'Debt write-off', value: DeductionCreditReason.DEBT_WRITE_OFF },
  { label: 'External payment', value: DeductionCreditReason.EXTERNAL_PAYMENT },
]

type DeductionCreditPickerProps = {
  policy: Repayment
  reason: DeductionCreditReason | undefined
  setReason: HandlerOf<DeductionCreditReason>
  isDisabled?: boolean
  onSubmit: (data: any) => void
  onCancel: () => void
  isLoading: boolean
  confirmReady: boolean
  isVendor: boolean
}

export function DeductionCreditDropdown({
  policy,
  reason,
  setReason,
  isDisabled,
  onSubmit,
  onCancel,
  isLoading,
  confirmReady,
  isVendor,
}: DeductionCreditPickerProps) {
  const formProps = {
    onSubmit,
    onCancel,
    isLoading,
    confirmReady,
  }

  const creditOptions = isVendor
    ? DeductionCreditOptions.filter((o) => o.value === DeductionCreditReason.EXTERNAL_PAYMENT)
    : DeductionCreditOptions

  useLayoutEffect(() => {
    if (isVendor) setReason(DeductionCreditReason.EXTERNAL_PAYMENT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FlexBox isFullHeight isColumn alignItems="flex-start">
      <Text variant="header2" mb={4} align="left">
        Credit policy
      </Text>
      <StyledDropdown
        label="Select a reason"
        options={creditOptions}
        value={reason}
        onChange={setReason}
        isDisabled={isDisabled}
        backgroundColor={bgGray}
        isFullWidth
        noMargin
      />
      {reason && (
        <>
          <Divider mt={4} mb={4} width="100%" />

          {reason === DeductionCreditReason.CORRECTION &&
            (policy.repayment_details.policy_type === VendorProgramPolicyType.ADVANCE ? (
              <CorrectionAdvanceForm policy={policy} {...formProps} />
            ) : (
              <CorrectionSubscriptionForm policy={policy} {...formProps} />
            ))}
          {reason === DeductionCreditReason.EXTERNAL_PAYMENT && <ExternalPaymentForm {...formProps} />}
          {reason === DeductionCreditReason.DEBT_WRITE_OFF && <DebtWriteOffForm {...formProps} />}
        </>
      )}
    </FlexBox>
  )
}

const StyledDropdown = styled(Dropdown)`
  width: 424px;
`
